import React, { useState, useRef } from 'react';
import { uploadToBlob, uploadMultipleToBlob } from '../../utils/blobStorage';

const ImageUploader = ({ onUploadComplete, multiple = false, maxFiles = 7, className = '' }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const files = e.target.files;
    
    if (!files || files.length === 0) {
      return;
    }
    
    // Validate file count if multiple
    if (multiple && files.length > maxFiles) {
      setError(`You can only upload up to ${maxFiles} files at once.`);
      return;
    }
    
    // Validate file types
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const invalidFiles = Array.from(files).filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setError('Only JPG, PNG, WebP, and GIF images are allowed.');
      return;
    }
    
    try {
      setIsUploading(true);
      setError(null);
      setUploadProgress(10); // Start progress
      
      let uploadedUrls;
      
      if (multiple) {
        // Upload multiple files
        uploadedUrls = await uploadMultipleToBlob(files, 'product');
      } else {
        // Upload single file
        const url = await uploadToBlob(files[0]);
        uploadedUrls = [url];
      }
      
      setUploadProgress(100);
      
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Call the callback with the uploaded URLs
      onUploadComplete(multiple ? uploadedUrls : uploadedUrls[0]);
      
      // Reset progress after a delay
      setTimeout(() => {
        setUploadProgress(0);
        setIsUploading(false);
      }, 1000);
      
    } catch (error) {
      console.error('Upload error:', error);
      setError('Failed to upload image(s). Please try again.');
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className={`${className}`}>
      <div className="relative">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple={multiple}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isUploading}
        />
        <div className={`bg-secondary border border-gray-800 rounded-md px-4 py-3 text-center ${isUploading ? 'opacity-70' : 'hover:border-primary'}`}>
          <div className="flex flex-col items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-light font-medium">
              {isUploading ? 'Uploading...' : multiple ? `Upload Images (max ${maxFiles})` : 'Upload Image'}
            </span>
            <span className="text-gray-400 text-sm mt-1">
              {multiple ? 'Click or drag files here' : 'Click or drag a file here'}
            </span>
          </div>
        </div>
        
        {/* Progress bar */}
        {uploadProgress > 0 && (
          <div className="mt-2 h-2 w-full bg-gray-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-300 ease-out"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        )}
      </div>
      
      {/* Error message */}
      {error && (
        <div className="mt-2 text-red-400 text-sm">
          {error}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
